/** Voice-profile contracts from api-scriptix #4171.
 *
 * Shapes mirror `docs/voice-profiles.md` and the routers under
 * `apps/api2/routers/v3/{speaker,document}/voice_profile*.py`. Embeddings are
 * never returned by the API, so nothing here carries biometric data — only
 * sample metadata, consent bookkeeping and review suggestions. */

/** `GET /organization/speakers/voice-profiles/capabilities`.
 *
 * Deliberately exposes no keys, model paths or threshold values: it answers
 * "may this user enroll, and within what limits", nothing else. */
export interface VoiceProfileCapabilities {
  /** Deployment switch AND the organization's own opt-in. */
  enabled: boolean;
  /** Enabled and the operator has model + encryption key configured. */
  enrollment_available: boolean;
  /** Enrollment-ready and the matching thresholds are calibrated. */
  recognition_available: boolean;
  /** The caller may enroll: enabled, configured and an organization admin. */
  can_enroll: boolean;
  consent_required: boolean;
  min_sample_seconds: number;
  max_sample_seconds: number;
  max_samples_per_speaker: number;
  /** Human-readable, e.g. "mono 16 kHz PCM16 WAV". */
  audio_format: string;
}

/** One enrolled sample's metadata — `GET /{speaker_id}/voice-profiles`. */
export interface VoiceProfileSample {
  id: number;
  /** Optional recording-condition label ("Meeting room"). */
  label: string | null;
  model_version: string;
  consent_recorded_at: string | null;
  consent_recorded_by_user_id: number | null;
  consent_notice_version: string | null;
  created_at: string | null;
  last_modified: string | null;
}

export interface VoiceProfileSampleList {
  count: number;
  profiles: VoiceProfileSample[];
}

/** Recognition job status from `GET .../voice-identifications`. */
export type VoiceIdentificationStatus =
  | 'not_requested'
  | 'queued'
  | 'running'
  | 'completed'
  | 'skipped'
  | 'failed';

/** Statuses the poller should keep watching; anything else is terminal. */
export const ACTIVE_IDENTIFICATION_STATUSES: readonly VoiceIdentificationStatus[] =
  ['queued', 'running'];

export const isActiveIdentificationStatus = (
  status: VoiceIdentificationStatus | undefined
): boolean =>
  !!status &&
  ACTIVE_IDENTIFICATION_STATUSES.includes(status as VoiceIdentificationStatus);

/** Per-label suggestion and human decision.
 *
 * `override_set` is the authoritative human-decision flag; `reason` only
 * describes the stored model suggestion ("matched", "manual",
 * "speaker_removed", …) and must never be read as a decision. */
export interface VoiceIdentification {
  speaker_label: string;
  suggested_speaker_id: number | null;
  /** A suggestion score, not a probability — never present it as certainty. */
  similarity: number | null;
  reason: string;
  override_set: boolean;
  override_speaker_id: number | null;
  /** Override when set, else the suggestion. Null means unknown. */
  effective_speaker_id: number | null;
}

export interface VoiceIdentificationsResponse {
  status: VoiceIdentificationStatus;
  reason: string | null;
  speakers: VoiceIdentification[];
}

/** One original diarization label and its source-audio window, in ms.
 *  `GET .../voice-identification-timeline` returns these sorted by start. */
export interface VoiceTimelineSpan {
  speaker_label: string;
  start_ms: number;
  stop_ms: number;
}

/** A selected passage of source audio, in source-audio milliseconds. */
export interface VoiceSampleRange {
  start_ms: number;
  stop_ms: number;
}

/** `POST .../voice-profile-sample-plan` — the backend's clean-speech plan. */
export interface VoiceSamplePlan {
  speaker_label: string;
  duration_ms: number;
  ranges: VoiceSampleRange[];
}

/** An in-memory preview cut by the server. `bytes` are the exact bytes to
 *  submit on enrollment — the browser never re-encodes audio. */
export interface VoiceSamplePreview {
  bytes: ArrayBuffer;
  /** Server-reported SHA-256, identifying the exact preview. */
  sha256: string | null;
  durationMs: number;
  ranges: VoiceSampleRange[];
}

/** Consent captured in the dialog; sent as request headers on enrollment. */
export interface VoiceConsent {
  confirmed: boolean;
  noticeVersion: string;
}

/** The consent notice this UI shows. Bump when the wording changes so the
 *  stored `consent_notice_version` identifies what the admin agreed to. */
export const VOICE_CONSENT_NOTICE_VERSION = 'panel-2026-10-29';

export interface AddVoiceSamplePayload {
  speakerId: number;
  bytes: ArrayBuffer;
  label?: string | null;
  consent: VoiceConsent;
}

export interface ReplaceVoiceSamplePayload extends AddVoiceSamplePayload {
  profileId: number;
}
